import { useEffect, useRef, useCallback } from "react";
import { useSSE } from "@/contexts/SSEContext";

/**
 * Hook that subscribes to an SSE event type and falls back to polling.
 *
 * - On mount: calls fetchFn() for initial state.
 * - When SSE delivers an event matching eventType + filterFn: calls onEvent(payload).
 * - Resets a 120s fallback timer on every SSE event.
 * - If 120s pass without SSE activity: fires fetchFn() once.
 * - If SSE is disconnected: switches to 60s polling automatically.
 * - When SSE reconnects: cancels polling, returns to SSE mode.
 *
 * @param {string} eventType - SSE event type to subscribe to
 * @param {function} onEvent - Called with SSE payload when event arrives
 * @param {function} fetchFn - HTTP fetch function for initial load + fallback
 * @param {function} filterFn - Optional filter (payload) => boolean
 */
export function useSSEWithFallback(eventType, onEvent, fetchFn, filterFn) {
  const { connectionStatus, subscribe, unsubscribe } = useSSE();
  const fallbackTimerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const connectionRef = useRef(connectionStatus);
  connectionRef.current = connectionStatus;

  const resetFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = setTimeout(() => {
      // 120s without SSE event — do one fallback fetch
      if (fetchFn) fetchFn();
    }, 120000);
  }, [fetchFn]);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(() => {
      if (fetchFn) fetchFn();
    }, 60000);
  }, [fetchFn]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Initial fetch on mount
    if (fetchFn) fetchFn();

    // SSE handler
    const handler = (payload) => {
      if (filterFn && !filterFn(payload?.data || payload)) return;
      resetFallbackTimer();
      if (onEvent) onEvent(payload?.data || payload);
    };

    subscribe(eventType, handler);
    resetFallbackTimer();

    return () => {
      unsubscribe(eventType, handler);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
      stopPolling();
    };
  }, [eventType, subscribe, unsubscribe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switch between SSE and polling based on connection status
  useEffect(() => {
    if (connectionStatus === "connected") {
      stopPolling();
      resetFallbackTimer();
    } else if (connectionStatus === "disconnected") {
      startPolling();
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    }
  }, [connectionStatus, stopPolling, startPolling, resetFallbackTimer]);
}
